import type { NavState } from "../lib/nav";
import {
  formatDistance,
  formatDuration,
  arrivalTime,
  maneuverInstruction,
} from "../lib/nav";
import { ManeuverIcon } from "./ManeuverIcon";

type Props = {
  state: NavState;
  onCancel: () => void;
};

export function NavStrip({ state, onCancel }: Props) {
  const { route, progress, destination, loading, rerouting, error } = state;

  if (loading) {
    return (
      <div className="nav-strip nav-strip-loading">
        <span className="nav-loader" />
        <span>Beregner rute til {destination?.name}…</span>
        <button className="close-btn" onClick={onCancel}>Annullér</button>
      </div>
    );
  }
  if (error) {
    return (
      <div className="nav-strip nav-strip-error">
        <span>⚠ {error}</span>
        <button className="close-btn" onClick={onCancel}>Luk</button>
      </div>
    );
  }
  if (!route) return null;

  const idx = progress?.currentStepIndex ?? 0;
  // Next maneuver is the *end* of the current step (= start of next).
  const nextStep = route.steps[Math.min(idx + 1, route.steps.length - 1)] || route.steps[idx];
  const dToNext = progress?.distanceToNextManeuver ?? nextStep.distance;
  const instruction = nextStep ? maneuverInstruction(nextStep) : "";

  const distRem = progress?.distanceRemaining ?? route.distance;
  const timeRem = progress?.timeRemaining ?? route.duration;

  return (
    <div className="nav-strip">
      {rerouting ? (
        <div className="nav-maneuver">
          <span className="nav-loader" />
          <div className="nav-text">
            <div className="nav-instruction">Genberegner rute…</div>
          </div>
        </div>
      ) : (
        <div className="nav-maneuver">
          <ManeuverIcon type={nextStep?.maneuver.type} modifier={nextStep?.maneuver.modifier} />
          <div className="nav-text">
            <div className="nav-distance tabular">{formatDistance(dToNext)}</div>
            <div className="nav-instruction">{instruction}</div>
          </div>
        </div>
      )}
      <div className="nav-summary">
        <div className="nav-summary-row">
          <span className="nav-summary-num tabular">{arrivalTime(timeRem)}</span>
          <span className="nav-summary-lbl">Ankomst</span>
        </div>
        <div className="nav-summary-row">
          <span className="nav-summary-num tabular">{formatDuration(timeRem)}</span>
          <span className="nav-summary-lbl">Tilbage</span>
        </div>
        <div className="nav-summary-row">
          <span className="nav-summary-num tabular">{formatDistance(distRem)}</span>
          <span className="nav-summary-lbl">Distance</span>
        </div>
      </div>
      <button className="nav-cancel" onClick={onCancel} aria-label="Stop navigation">
        <CloseIcon />
      </button>
    </div>
  );
}

function CloseIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden>
      <line x1="6" y1="6" x2="18" y2="18" />
      <line x1="18" y1="6" x2="6" y2="18" />
    </svg>
  );
}
